import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { Card, EmptyState, KPI, PageHeader, btnAccent, inputCls } from '../components/ui';
import {
  ActivityIcon, ChevronRightIcon, ClockIcon, FolderIcon, KeyIcon,
  PlusIcon, RefreshIcon, TrashIcon, UsersIcon, ZapIcon,
} from '../components/icons';
import { timeAgo } from '../lib/time';
import type { Project } from '../api/types';

export function ProjectsPage() {
  const qc = useQueryClient();
  const projects = useQuery({
    queryKey: ['projects'],
    queryFn: api.listProjects,
    refetchInterval: 60_000,
  });
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api.createProject(name),
    onSuccess: () => { setName(''); qc.invalidateQueries({ queryKey: ['projects'] }); },
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    create.mutate();
  }

  const totalEvents = (projects.data ?? []).reduce((n, p) => n + p.events_24h, 0);
  const totalSessions = (projects.data ?? []).reduce((n, p) => n + p.sessions_24h, 0);
  const projectCount = projects.data?.length ?? 0;

  return (
    <div>
      <PageHeader
        icon={FolderIcon}
        title="Projects"
        subtitle="One project per environment — dev / staging / prod"
        actions={
          <form onSubmit={onSubmit} className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="New project name…" className={`${inputCls} w-56`} />
            <button className={btnAccent} disabled={!name.trim() || create.isPending}>
              <PlusIcon size={14} /> Create
            </button>
          </form>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KPI label="Projects"       value={projectCount}                    icon={FolderIcon} />
        <KPI label="Events (24h)"   value={totalEvents.toLocaleString()}    icon={ZapIcon}  tone="bad" />
        <KPI label="Sessions (24h)" value={totalSessions.toLocaleString()}  icon={UsersIcon} />
      </div>

      {projects.isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-5 animate-pulse">
              <div className="h-4 bg-slate-100 rounded w-1/3 mb-3" />
              <div className="h-3 bg-slate-100 rounded w-2/3" />
            </Card>
          ))}
        </div>
      )}

      {projects.data && projects.data.length === 0 && (
        <Card>
          <EmptyState icon={FolderIcon}>
            No projects yet — create your first one above.
          </EmptyState>
        </Card>
      )}

      {projects.data && projects.data.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.data.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ project: p }: { project: Project }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState(p.name);
  const hot = p.events_24h > 0;

  const invalidate = () => qc.invalidateQueries({ queryKey: ['projects'] });

  const rename = useMutation({
    mutationFn: () => api.patchProject(p.id, { name: newName }),
    onSuccess: () => { setEditing(false); invalidate(); },
  });
  const wipe = useMutation({
    mutationFn: () => api.wipeProject(p.id),
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: () => api.deleteProject(p.id),
    onSuccess: invalidate,
  });

  const onWipe = () => {
    if (window.confirm(`Wipe ALL events/sessions for "${p.name}"?\nProject + api key stay; data gone.`)) {
      wipe.mutate();
    }
  };
  const onDelete = () => {
    const expected = p.name;
    const typed = window.prompt(
      `Delete project "${expected}" forever?\nThis removes ALL data + the api key.\nType the project name to confirm:`,
    );
    if (typed === expected) del.mutate();
  };
  const onRenameSubmit = (e: FormEvent) => {
    e.preventDefault();
    const v = newName.trim();
    if (!v || v === p.name) { setEditing(false); return; }
    rename.mutate();
  };

  return (
    <Card className="p-5 h-full hover:border-accent-500/40 hover:shadow-md transition group">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {editing ? (
            <form onSubmit={onRenameSubmit} className="flex gap-1">
              <input value={newName} onChange={(e) => setNewName(e.target.value)}
                     autoFocus onKeyDown={(e) => e.key === 'Escape' && setEditing(false)}
                     className="font-semibold flex-1 min-w-0 border border-slate-300 rounded px-2 h-7 text-sm" />
              <button className="h-7 px-2 text-xs bg-slate-900 text-white rounded">save</button>
              <button type="button" onClick={() => { setEditing(false); setNewName(p.name); }}
                      className="h-7 px-2 text-xs text-slate-500 hover:text-slate-900">cancel</button>
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <Link to={`/projects/${p.id}/errors`}
                    className="font-semibold text-slate-900 truncate hover:text-accent-700">
                {p.name}
              </Link>
              {hot && (
                <span className="inline-flex items-center text-[10px] font-semibold uppercase
                                 tracking-wider px-1.5 py-0.5 rounded ring-1 ring-rose-200 bg-rose-50 text-rose-700">
                  active
                </span>
              )}
            </div>
          )}
          <p className="text-xs text-slate-500 mt-1">
            {p.last_event_at ? <>Last event {timeAgo(p.last_event_at)}</> : <>No events yet</>}
          </p>
        </div>
        <Link to={`/projects/${p.id}/errors`}>
          <ChevronRightIcon size={18} className="text-slate-300 group-hover:text-accent-500 transition" />
        </Link>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
        <StatLine icon={ZapIcon}   label="Events 24h" value={p.events_24h.toLocaleString()}  tone={hot ? 'bad' : 'mute'} />
        <StatLine icon={UsersIcon} label="Sessions"   value={p.sessions_24h.toLocaleString()} />
      </div>

      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
        <KeyIcon size={12} />
        <code className="font-mono truncate flex-1">{p.api_key}</code>
        <CopyButton value={p.api_key} />
        <span className="text-slate-300">·</span>
        <ClockIcon size={11} />
        <span>{p.created_at.slice(0, 10)}</span>
      </div>

      {/* Hover-revealed admin actions */}
      <div className="mt-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        <button onClick={() => setEditing(true)} disabled={editing} title="Rename"
                className="h-7 px-2 inline-flex items-center gap-1 text-xs text-slate-500
                           hover:text-slate-900 hover:bg-slate-100 rounded transition disabled:opacity-30">
          rename
        </button>
        <span className="text-slate-300">·</span>
        <button onClick={onWipe} disabled={wipe.isPending} title="Delete all events/sessions"
                className="h-7 px-2 inline-flex items-center gap-1 text-xs text-amber-700
                           hover:bg-amber-50 rounded transition">
          <RefreshIcon size={11} /> wipe data
        </button>
        <span className="text-slate-300">·</span>
        <button onClick={onDelete} disabled={del.isPending} title="Delete project"
                className="h-7 px-2 inline-flex items-center gap-1 text-xs text-rose-700
                           hover:bg-rose-50 rounded transition">
          <TrashIcon size={11} /> delete
        </button>
      </div>
    </Card>
  );
}

// CopyButton — small inline button that copies a value to the clipboard
// and flashes "copied!" for 1.5s. Stops parent <Link> nav when clicked.
function CopyButton({ value }: { value: string }) {
  const [done, setDone] = useState(false);
  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      // Fallback for browsers without clipboard API / non-secure contexts
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setDone(true); setTimeout(() => setDone(false), 1500); }
      catch { /* swallow */ }
      finally { document.body.removeChild(ta); }
    }
  };
  return (
    <button onClick={onClick}
            className={
              'inline-flex items-center gap-1 h-6 px-1.5 rounded text-[11px] font-medium transition ' +
              (done
                ? 'text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100')
            }
            title="Copy API key">
      {done ? 'copied!' : 'copy'}
    </button>
  );
}

function StatLine({ icon: Icon, label, value, tone = 'default' }: {
  icon: typeof ActivityIcon; label: string; value: string; tone?: 'default' | 'bad' | 'mute';
}) {
  const toneCls = tone === 'bad' ? 'text-rose-600'
                : tone === 'mute' ? 'text-slate-400'
                : 'text-slate-900';
  return (
    <div className="flex items-center gap-2">
      <Icon size={14} className={toneCls} />
      <div className="min-w-0">
        <p className="text-[11px] text-slate-500 uppercase tracking-wider">{label}</p>
        <p className={`font-semibold tabular-nums ${toneCls}`}>{value}</p>
      </div>
    </div>
  );
}
